import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CWE.css';
import axiosInstance from '../../api';

/* Human-readable names for the most common CWE IDs */
const CWE_NAMES = {
  'CWE-79':  'Cross-site Scripting (XSS)',
  'CWE-89':  'SQL Injection',
  'CWE-787': 'Out-of-bounds Write',
  'CWE-22':  'Path Traversal',
  'CWE-416': 'Use After Free',
  'CWE-862': 'Missing Authorization',
  'CWE-20':  'Improper Input Validation',
  'CWE-78':  'OS Command Injection',
  'CWE-125': 'Out-of-bounds Read',
  'CWE-120': 'Buffer Copy Without Size Check',
  'CWE-77':  'Command Injection',
  'CWE-94':  'Code Injection',
  'CWE-434': 'Unrestricted File Upload',
  'CWE-287': 'Improper Authentication',
  'CWE-352': 'Cross-Site Request Forgery (CSRF)',
  'CWE-190': 'Integer Overflow',
  'CWE-476': 'NULL Pointer Dereference',
  'CWE-400': 'Uncontrolled Resource Consumption',
  'CWE-502': 'Deserialization of Untrusted Data',
  'CWE-601': 'Open Redirect',
};

const TOP_CWE_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6',
  '#ec4899', '#6366f1',
];

function CWE() {
  const { t } = useTranslation();
  const [cweData,  setCweData]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    axiosInstance.get('/cwe/summary', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setCweData(res.data || []); setLoading(false); })
      .catch(e  => { setError(e.message);        setLoading(false); });
  }, []);

  const real = cweData.filter(r => r.cweId && r.cweId.startsWith('CWE-'));
  const totalEntries = real.reduce((s, r) => s + (r.totalCount || 0), 0);
  const top10 = real.slice(0, 10);

  const filtered = real.filter(r =>
    !search ||
    r.cweId.toLowerCase().includes(search.toLowerCase()) ||
    (CWE_NAMES[r.cweId] || '').toLowerCase().includes(search.toLowerCase())
  );

  if (error) return (
    <div className="cwe-error">
      <i className="fas fa-exclamation-circle" /> {t('CWE.errorLoading', { error })}
    </div>
  );

  return (
    <div className="cwe-page">
      {/* Summary stats */}
      <div className="cwe-stats">
        <div className="cwe-stat-card">
          <div className="cwe-stat-value">{real.length}</div>
          <div className="cwe-stat-label">{t('CWE.statUniqueCwes')}</div>
        </div>
        <div className="cwe-stat-card">
          <div className="cwe-stat-value">{totalEntries.toLocaleString()}</div>
          <div className="cwe-stat-label">{t('CWE.statTotalWeaknessEntries')}</div>
        </div>
        <div className="cwe-stat-card">
          <div className="cwe-stat-value cwe-top1">{top10[0]?.cweId || '—'}</div>
          <div className="cwe-stat-label">{t('CWE.statMostCommon')}</div>
        </div>
        <div className="cwe-stat-card">
          <div className="cwe-stat-value">{top10[0]?.totalCount?.toLocaleString() || '—'}</div>
          <div className="cwe-stat-label">{t('CWE.statTopCweCount')}</div>
        </div>
      </div>

      {/* Top 10 bar chart */}
      {!loading && top10.length > 0 && (
        <div className="cwe-chart-card">
          <div className="cwe-chart-title">{t('CWE.chartTitleTop10')}</div>
          <div className="cwe-bars">
            {top10.map((row, i) => {
              const pct = Math.round((row.totalCount / top10[0].totalCount) * 100);
              return (
                <div key={row.cweId} className="cwe-bar-row">
                  <div className="cwe-bar-label">
                    <a
                      href={`https://cwe.mitre.org/data/definitions/${row.cweId.replace('CWE-','')}.html`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cwe-id-link"
                    >
                      {row.cweId}
                    </a>
                    <span className="cwe-bar-name">{CWE_NAMES[row.cweId] || ''}</span>
                  </div>
                  <div className="cwe-bar-track">
                    <div
                      className="cwe-bar-fill"
                      style={{ width: `${pct}%`, background: TOP_CWE_COLORS[i] }}
                    />
                  </div>
                  <div className="cwe-bar-count">{row.totalCount.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search + table */}
      <div className="cwe-toolbar">
        <div className="cwe-search-wrap">
          <i className="fas fa-search cwe-search-icon" />
          <input
            className="cwe-search"
            placeholder={t('CWE.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="cwe-search-clear" onClick={() => setSearch('')}>
              <i className="fas fa-times" />
            </button>
          )}
        </div>
        <span className="cwe-result-count">{t('CWE.resultCount', { count: filtered.length })}</span>
      </div>

      <div className="cwe-table-wrap">
        {loading ? (
          <div className="cwe-loading">
            <i className="fas fa-circle-notch fa-spin" /> {t('CWE.loading')}
          </div>
        ) : filtered.length === 0 ? (
          <div className="cwe-empty">{t('CWE.emptyNoMatch')}</div>
        ) : (
          <table className="cwe-table">
            <thead>
              <tr>
                <th style={{ width: '12%' }}>{t('CWE.tableHeaderCweId')}</th>
                <th>{t('CWE.tableHeaderNameDescription')}</th>
                <th style={{ width: '10%', textAlign: 'right' }}>{t('CWE.tableHeaderPrimary')}</th>
                <th style={{ width: '10%', textAlign: 'right' }}>{t('CWE.tableHeaderSecondary')}</th>
                <th style={{ width: '10%', textAlign: 'right' }}>{t('CWE.tableHeaderTotal')}</th>
                <th style={{ width: '5%'  }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => {
                const name = CWE_NAMES[row.cweId];
                const isExp = expanded === i;
                return (
                  <React.Fragment key={row.cweId}>
                    <tr
                      className={`cwe-row${isExp ? ' cwe-row-expanded' : ''}`}
                      onClick={() => setExpanded(isExp ? null : i)}
                    >
                      <td>
                        <a
                          href={`https://cwe.mitre.org/data/definitions/${row.cweId.replace('CWE-','')}.html`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cwe-id-link"
                          onClick={e => e.stopPropagation()}
                        >
                          {row.cweId}
                        </a>
                      </td>
                      <td>
                        {name
                          ? <span className="cwe-name">{name}</span>
                          : <span className="cwe-name-unknown">—</span>
                        }
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="cwe-count-primary">{row.primaryCount?.toLocaleString()}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="cwe-count-secondary">{row.secondaryCount?.toLocaleString()}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <strong>{row.totalCount?.toLocaleString()}</strong>
                      </td>
                      <td>
                        <i className={`fas fa-chevron-${isExp ? 'up' : 'down'} cwe-chevron`} />
                      </td>
                    </tr>
                    {isExp && (
                      <tr className="cwe-detail-row">
                        <td colSpan={6}>
                          <div className="cwe-detail">
                            <div className="cwe-detail-grid">
                              <div className="cwe-detail-item">
                                <span className="cwe-detail-key">{t('CWE.detailKeyCweId')}</span>
                                <span className="cwe-detail-val">{row.cweId}</span>
                              </div>
                              <div className="cwe-detail-item">
                                <span className="cwe-detail-key">{t('CWE.detailKeyName')}</span>
                                <span className="cwe-detail-val">{name || t('CWE.detailValueUnknown')}</span>
                              </div>
                              <div className="cwe-detail-item">
                                <span className="cwe-detail-key">{t('CWE.detailKeyPrimaryReferences')}</span>
                                <span className="cwe-detail-val">{row.primaryCount}</span>
                              </div>
                              <div className="cwe-detail-item">
                                <span className="cwe-detail-key">{t('CWE.detailKeySecondaryReferences')}</span>
                                <span className="cwe-detail-val">{row.secondaryCount}</span>
                              </div>
                            </div>
                            <a
                              href={`https://cwe.mitre.org/data/definitions/${row.cweId.replace('CWE-','')}.html`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cwe-detail-link"
                            >
                              <i className="fas fa-external-link-alt" /> {t('CWE.viewOnMitre')}
                            </a>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default CWE;
